import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Navigation } from "@/components/ui/navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useAnalytics } from "@/hooks/useAnalytics";
import { fetchPracticeSummary, mapQuestionRow, type PracticeQuestion } from "@/lib/api/practice";
import { fetchSubjectOutline } from "@/lib/api/subjects";
import { LatexText } from "@/components/ui/latex-text";

type OutlineUnit = Awaited<ReturnType<typeof fetchSubjectOutline>>["units"][number];
type OutlineTopic = OutlineUnit["topics"][number];

const DIFFICULTY_LABELS: Record<string, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  all: "All",
  unknown: "Unknown",
};

type AttemptSummary = {
  questionId: string;
  question: PracticeQuestion | null;
  selectedIndex: number | null;
  correctIndex: number | null;
  isCorrect: boolean | null;
  elapsedSeconds: number | null;
  createdAt: string | null;
};

const EMPTY_UNITS: OutlineUnit[] = [];

export default function PracticeSummary() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("id");
  const { user } = useAuth();
  const { trackPracticeSessionCompleted } = useAnalytics();

  const enabled = Boolean(sessionId && user);

  const summaryQuery = useQuery({
    queryKey: ["practice-summary", sessionId],
    queryFn: () => fetchPracticeSummary(sessionId!),
    enabled,
    retry: 1,
  });

  const outlineQuery = useQuery({
    queryKey: ["subject-outline", slug],
    queryFn: () => fetchSubjectOutline(slug!),
    enabled: Boolean(slug && user),
  });

  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});
  const toggleOpen = (qid: string) => setOpenIds((prev) => ({ ...prev, [qid]: !prev[qid] }));

  const units = outlineQuery.data?.units ?? EMPTY_UNITS;

  const unitMap = useMemo(() => {
    const map = new Map<string, OutlineUnit>();
    units.forEach((unit) => map.set(unit.id, unit));
    return map;
  }, [units]);

  const topicMap = useMemo(() => {
    const map = new Map<string, OutlineTopic>();
    units.forEach((unit) => {
      unit.topics.forEach((topic) => {
        if (topic.id) {
          map.set(topic.id, topic);
        }
      });
    });
    return map;
  }, [units]);

  // Track session completion once when data loads
  const trackedRef = useRef<string | null>(null);
  const summaryData = summaryQuery.data;
  
  useEffect(() => {
    if (!sessionId || !slug || !summaryData) return;
    if (trackedRef.current === sessionId) return; // Already tracked this session
    
    const attempts = summaryData.attempts ?? [];
    const total = attempts.length;
    if (total === 0) return;
    
    const correctCount = attempts.filter((a) => a.is_correct === true).length;
    const pct = Math.round((correctCount / total) * 100);
    const totalTimeSeconds = attempts.reduce((sum, a) => sum + (typeof a.time_taken_seconds === "number" ? a.time_taken_seconds : 0), 0);
    
    trackPracticeSessionCompleted({
      subjectSlug: slug,
      questionCount: total,
      correctCount,
      accuracy: pct,
      totalTimeSeconds,
    });
    
    trackedRef.current = sessionId;
  }, [sessionId, slug, summaryData, trackPracticeSessionCompleted]);

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Practice summaries</CardTitle>
              <CardDescription>Sign in to review completed practice sessions.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/auth">Sign in</Link>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle>No session selected</CardTitle>
              <CardDescription>Choose a practice session from the dashboard or start a new one.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to={`/practice/${slug}/setup`} state={{ from: location.pathname + location.search }}>
                  Go to setup
                </Link>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (summaryQuery.isLoading || outlineQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Loading session…</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-32 rounded-lg bg-muted/40 animate-pulse" />
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle>Unable to load summary</CardTitle>
              <CardDescription>The session may have been deleted or is unavailable. Try again later.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate(-1)}>Go back</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const session = summaryQuery.data.session;
  const attempts = summaryQuery.data.attempts ?? [];

  const config = (session?.config as { topicIds?: string[]; difficulty?: string; count?: number } | null) ?? {};
  const unitLabels = (session?.unit_ids ?? []).map((unitId) => {
    const unit = unitMap.get(unitId);
    return (unit?.name ?? (unit as { title?: string })?.title ?? unitId) as string;
  });
  const topicLabels = (config.topicIds ?? []).map((topicId) => {
    const topic = topicMap.get(topicId);
    return (topic?.title ?? (topic as { name?: string })?.name ?? topicId) as string;
  });
  const difficultyLabel = DIFFICULTY_LABELS[(config.difficulty ?? "all").toLowerCase()] ?? config.difficulty ?? "All";

  const attemptSummaries: AttemptSummary[] = attempts.map((attempt) => {
    const questionRow = attempt.questions as Parameters<typeof mapQuestionRow>[0] | null;
    const question = questionRow ? mapQuestionRow(questionRow) : null;
    const answerValue = attempt.answer && typeof attempt.answer === "object" ? (attempt.answer as { choice?: number | null }) : null;
    const selectedIndex = typeof answerValue?.choice === "number" ? answerValue.choice : null;
    const correctIndex = question?.correctIndex ?? (typeof questionRow?.answer_index === "number" ? questionRow.answer_index : null);
    const isCorrect = typeof attempt.is_correct === "boolean" ? attempt.is_correct : selectedIndex != null && correctIndex != null ? selectedIndex === correctIndex : null;
    const elapsedSeconds = typeof attempt.time_taken_seconds === "number" ? attempt.time_taken_seconds : null;
    return {
      questionId: attempt.question_id,
      question,
      selectedIndex,
      correctIndex,
      isCorrect,
      elapsedSeconds,
      createdAt: attempt.created_at,
    };
  });

  const total = attemptSummaries.length;
  const correctCount = attemptSummaries.filter((item) => item.isCorrect === true).length;
  const incorrectCount = attemptSummaries.filter((item) => item.isCorrect === false).length;
  const unansweredCount = total - correctCount - incorrectCount;

  const timed = attemptSummaries.filter((item) => typeof item.elapsedSeconds === "number" && item.elapsedSeconds !== null);
  const avgSeconds = timed.length ? Math.round(timed.reduce((sum, item) => sum + (item.elapsedSeconds ?? 0), 0) / timed.length) : null;
  const fastest = timed.length
    ? timed.reduce((min, item) => (min.elapsedSeconds! <= (item.elapsedSeconds ?? Infinity) ? min : item))
    : null;
  const slowest = timed.length
    ? timed.reduce((max, item) => (max.elapsedSeconds! >= (item.elapsedSeconds ?? -Infinity) ? max : item))
    : null;

  const skillStats = Array.from(
    attemptSummaries.reduce((map, item) => {
      const key = item.question?.kcRef ?? "Unknown";
      const entry = map.get(key) ?? { key, correct: 0, total: 0 };
      entry.total += 1;
      if (item.isCorrect === true) entry.correct += 1;
      map.set(key, entry);
      return map;
    }, new Map<string, { key: string; correct: number; total: number }>() ).values(),
  );

  const pct = total > 0 ? Math.round((correctCount / total) * 100) : 0;
  const avgTimeLabel = avgSeconds != null ? `${avgSeconds}s` : "–";
  const requestedCount = config.count ?? total;
  const servedCount = total;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-md" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Badge variant="secondary" className="ml-1">Summary</Badge>
          </div>
        </div>

        <Card className="border">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl">Your performance</CardTitle>
            <CardDescription>
              {unitLabels.length ? unitLabels.join(", ") : "Custom selection"} • {topicLabels.length ? topicLabels.join(", ") : "All topics"} • {difficultyLabel} • Requested {requestedCount}{" "}
              {servedCount !== requestedCount ? `(served ${servedCount} after exclusions)` : ""}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryStat label="Score" value={`${correctCount}/${total || 1}`} subtle={`${pct}%`} color="primary" />
              <SummaryStat label="Correct" value={String(correctCount)} color="emerald" />
              <SummaryStat label="Incorrect" value={String(incorrectCount)} color="rose" />
              <SummaryStat label="Unanswered" value={String(unansweredCount)} color="amber" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryStat label="Avg time" value={avgTimeLabel} color="sky" />
              {fastest && (
                <SummaryStat
                  label="Fastest"
                  value={`${Math.round(fastest.elapsedSeconds ?? 0)}s`}
                  subtle={`Question #${attemptSummaries.findIndex((item) => item.questionId === fastest.questionId) + 1}`}
                  color="violet"
                />
              )}
              {slowest && (
                <SummaryStat
                  label="Slowest"
                  value={`${Math.round(slowest.elapsedSeconds ?? 0)}s`}
                  subtle={`Question #${attemptSummaries.findIndex((item) => item.questionId === slowest.questionId) + 1}`}
                  color="fuchsia"
                />
              )}
            </div>
            <Separator />
            {skillStats.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium">Skills analysis</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {skillStats
                    .slice()
                    .sort((a, b) => (b.correct / Math.max(b.total, 1)) - (a.correct / Math.max(a.total, 1)))
                    .map((skill) => {
                      const skillPct = skill.total > 0 ? Math.round((skill.correct / skill.total) * 100) : 0;
                      const borderClass = skillPct >= 70 ? "border-emerald-300" : skillPct >= 40 ? "border-amber-300" : "border-rose-300";
                      const chipClass = skillPct >= 70 ? "bg-emerald-500" : skillPct >= 40 ? "bg-amber-500" : "bg-rose-500";
                      return (
                        <Card key={skill.key} className={`border ${borderClass}`}>
                          <CardContent className="p-3 space-y-1.5">
                            <div className="text-xs text-muted-foreground flex items-center justify-between">
                              <span>{skill.key}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded text-white ${chipClass}`}>{skillPct}%</span>
                            </div>
                            <div className="text-sm font-medium">
                              <span className={`mr-2 inline-block rounded px-2 py-0.5 text-white ${chipClass}`}>{skill.correct}</span>
                              <span className="text-muted-foreground">/ {skill.total} correct</span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                </div>
              </div>
            )}
            <Separator />
            <div className="space-y-2">
              <div className="text-sm font-medium">Question review</div>
              <div className="space-y-2">
                {attemptSummaries.map((item, index) => (
                  <Card key={`${item.questionId}-${index}`} className="border">
                    <CardContent className="p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {item.isCorrect === true ? (
                            <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Correct</span>
                          ) : item.isCorrect === false ? (
                            <span className="text-xs px-2 py-0.5 rounded bg-rose-100 text-rose-700">Incorrect</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">Unanswered</span>
                          )}
                          <span>Q{index + 1}</span>
                          {item.question?.kcRef && <span className="text-muted-foreground text-xs">{item.question.kcRef}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          {item.elapsedSeconds != null && (
                            <span className="text-muted-foreground text-xs">{Math.round(item.elapsedSeconds)}s</span>
                          )}
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => toggleOpen(item.questionId)}>
                            {openIds[item.questionId] ? (
                              <>Hide <ChevronUp className="h-4 w-4 ml-1" /></>
                            ) : (
                              <>Show full <ChevronDown className="h-4 w-4 ml-1" /></>
                            )}
                          </Button>
                        </div>
                      </div>
                      {openIds[item.questionId] && (
                        <div className="mt-3 space-y-2">
                          {item.question ? (
                            <>
                              <div className="text-base"><LatexText text={item.question.stem} /></div>
                              <div className="space-y-2">
                                {item.question.choices.map((choice, choiceIndex) => {
                                  const selected = item.selectedIndex === choiceIndex;
                                  const isCorrectChoice = item.correctIndex != null && choiceIndex === item.correctIndex;
                                  const isIncorrectSelected = selected && !isCorrectChoice;
                                  const border = isCorrectChoice
                                    ? "border-emerald-500 bg-emerald-50"
                                    : isIncorrectSelected
                                    ? "border-rose-500 bg-rose-50"
                                    : selected
                                    ? "border-primary bg-primary/5"
                                    : "border-muted";
                                  return (
                                    <div key={choiceIndex} className="space-y-1">
                                      <div className={`flex items-center justify-between rounded-md border ${border} px-3 py-2`}>
                                        <div className="flex items-center gap-3">
                                          <div className={`h-7 w-7 rounded-full flex items-center justify-center border ${selected ? "border-primary text-primary" : "border-muted-foreground/30 text-muted-foreground"} bg-background`}>
                                            {String.fromCharCode(65 + choiceIndex)}
                                          </div>
                                          <div><LatexText text={choice.text} /></div>
                                        </div>
                                      </div>
                                      {choice.reason && (
                                        <div className="pl-12 pr-3 pb-1">
                                          <div className="text-xs text-muted-foreground italic">
                                            <LatexText text={choice.reason} />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          ) : (
                            <div className="text-xs text-muted-foreground">Full question text not available.</div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button asChild>
                <Link to={`/practice/${slug}/setup`} state={{ from: location.pathname + location.search }}>
                  Try another set
                </Link>
              </Button>
              <Button variant="outline" onClick={() => navigate(-1)}>Back</Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  subtle,
  color,
}: {
  label: string;
  value: string;
  subtle?: string;
  color?: "primary" | "emerald" | "rose" | "amber" | "sky" | "violet" | "fuchsia";
}) {
  const colorClass =
    color === "emerald"
      ? "bg-emerald-100 text-emerald-700"
      : color === "rose"
      ? "bg-rose-100 text-rose-700"
      : color === "amber"
      ? "bg-amber-100 text-amber-700"
      : color === "sky"
      ? "bg-sky-100 text-sky-700"
      : color === "violet"
      ? "bg-violet-100 text-violet-700"
      : color === "fuchsia"
      ? "bg-fuchsia-100 text-fuchsia-700"
      : "bg-primary/10 text-primary";

  return (
    <div className="rounded-md border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`inline-block text-2xl font-semibold px-2 rounded ${colorClass}`}>{value}</div>
      {subtle && <div className="text-xs text-muted-foreground">{subtle}</div>}
    </div>
  );
}
